/**
 * Predictive search.
 *
 * Talks to Shopify's own /search/suggest endpoint, so results are the same
 * ones the storefront search returns. Built for speed: every keystroke past
 * the second fires a debounced request, the previous request is aborted, and
 * answers are cached per query for the life of the page, so backing up over a
 * word never costs a round trip.
 *
 * It is a combobox, so it works from the keyboard: arrows move, Enter opens,
 * Escape closes and returns focus to the field.
 */
(() => {
  const MIN_CHARS = 2;
  const DEBOUNCE = 140;
  const LIMIT = 6;

  class PredictiveSearch {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('[data-nyl-search-input]');
      this.panel = root.querySelector('[data-nyl-search-panel]');
      this.clear = root.querySelector('[data-nyl-search-clear]');
      this.form = root.querySelector('form');
      if (!this.input || !this.panel) return;

      this.cache = new Map();
      this.controller = null;
      this.timer = null;
      this.index = -1;
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => { if (this.input.value.trim().length >= MIN_CHARS) this.open(); });
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.clear?.addEventListener('click', () => this.reset());
      document.addEventListener('pointerdown', (e) => {
        if (!this.root.contains(e.target)) this.close();
      });
    }

    onInput() {
      const q = this.input.value.trim();
      this.clear.hidden = q.length === 0;
      clearTimeout(this.timer);

      if (q.length < MIN_CHARS) {
        this.close();
        return;
      }
      this.timer = setTimeout(() => this.search(q), DEBOUNCE);
    }

    async search(q) {
      if (this.cache.has(q)) {
        this.paint(this.cache.get(q));
        return;
      }

      this.controller?.abort();
      this.controller = new AbortController();

      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}search/suggest?q=${encodeURIComponent(q)}`
        + '&resources[type]=product,collection,page,article'
        + `&resources[limit]=${LIMIT}`
        + '&resources[options][unavailable_products]=last'
        + '&section_id=nyl-predictive-search';

      try {
        const response = await fetch(url, {
          signal: this.controller.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(String(response.status));

        // The section comes back wrapped in its own shopify-section div.
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const markup = doc.querySelector('.shopify-section')?.innerHTML ?? '';

        this.cache.set(q, markup);
        this.paint(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        // A failed suggest is not worth an error state. The form still works.
        this.close();
      }
    }

    paint(markup) {
      if (!markup.trim()) {
        this.close();
        return;
      }
      this.panel.innerHTML = markup;
      this.index = -1;
      this.open();
    }

    get options() {
      return Array.from(this.panel.querySelectorAll('[role="option"], .nyl-search__all'));
    }

    open() {
      this.panel.hidden = false;
      this.root.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.root.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.index = -1;
    }

    reset() {
      this.input.value = '';
      this.clear.hidden = true;
      this.close();
      this.input.focus();
    }

    move(step) {
      const options = this.options;
      if (!options.length) return;
      options[this.index]?.classList.remove('is-active');
      this.index = (this.index + step + options.length) % options.length;
      const next = options[this.index];
      next.classList.add('is-active');
      next.scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', next.id || '');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.input.blur();
        return;
      }
      if (this.panel.hidden) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.move(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'Enter' && this.index > -1) {
        event.preventDefault();
        this.options[this.index].click();
      }
    }
  }

  const start = () => document.querySelectorAll('[data-nyl-search]').forEach((el) => new PredictiveSearch(el));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
